/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {Link} from '@/i18n/routing';
import dynamic from 'next/dynamic';

const ClientOnClick = dynamic(() => import('@/components/ClientOnClick'), { loading: () => null });

interface ShellButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string;
  children: React.ReactNode;
  className?: string;
  variant?: 'white' | 'gradient' | 'sale';
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  target?: string;
  wrapperClassName?: string;
  ariaLabel?: string;
}

const ShellButton = ({ href, children, className = '', variant = 'white', onClick, target, type, wrapperClassName, ariaLabel, ...rest }: ShellButtonProps) => {
  const buttonStyles = 
    variant === 'gradient'
      ? 'bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to text-text-main hover:from-primary-light hover:to-primary-dark font-semibold'
      : variant === 'sale'
      ? 'bg-gradient-to-r from-accent-dark to-accent text-text-main hover:from-accent hover:to-accent-light font-semibold shadow-lg shadow-accent/20'
      : 'bg-text-main text-bg-main hover:bg-primary-light/10 transition-colors';

  // If there's an onClick handler, wrap the content in a client component
  if (onClick) {
    return (
      <ClientOnClick onClick={onClick}>
        {href ? (
          <div className={`inline-block ${wrapperClassName ? wrapperClassName : "sm:self-center md:self-end"}`}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-full bg-text-main/5 backdrop-blur-sm shadow-[inset_0_0_15px_rgba(255,255,255,0.1)]"></div>
              <Link
                href={href as any}
                target={target}
                aria-label={ariaLabel}
                className={`relative inline-flex items-center px-4 py-3 rounded-full font-sm gap-2 transition-all duration-300 ${buttonStyles} ${className}`}
              >
                {children}
              </Link>
            </div>
          </div>
        ) : (
          <div className={`inline-block ${wrapperClassName ? wrapperClassName : "sm:self-center md:self-end"}`}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-full bg-text-main/5 backdrop-blur-sm shadow-[inset_0_0_15px_rgba(255,255,255,0.1)]"></div>
              <button
                type={type || "button"}
                aria-label={ariaLabel}
                className={`relative inline-flex items-center px-4 py-3 rounded-full font-sm gap-2 transition-all duration-300 ${buttonStyles} ${className}`}
                {...rest}
              >
                {children}
              </button>
            </div>
          </div>
        )}
      </ClientOnClick>
    );
  }

  // If there's no onClick, just render the server component
  if (href) {
    return (
      <div className={`inline-block ${wrapperClassName ? wrapperClassName : "sm:self-center md:self-end"}`}>
        <div className="relative">
          <div className="absolute -inset-3 rounded-full bg-text-main/5 backdrop-blur-sm shadow-[inset_0_0_15px_rgba(255,255,255,0.1)]"></div>
          <Link
            href={href as any}
            target={target}
            aria-label={ariaLabel}
            className={`relative inline-flex items-center px-4 py-3 rounded-full font-sm gap-2 transition-all duration-300 ${buttonStyles} ${className}`}
          >
            {children}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-block ${wrapperClassName ? wrapperClassName : "sm:self-center md:self-end"}`}>
      <div className="relative">
        <div className="absolute -inset-3 rounded-full bg-text-main/5 backdrop-blur-sm shadow-[inset_0_0_15px_rgba(255,255,255,0.1)]"></div>
        <button
          type={type || "button"}
          aria-label={ariaLabel}
          className={`relative inline-flex items-center px-4 py-3 rounded-full font-sm gap-2 transition-all duration-300 ${buttonStyles} ${className}`}
          {...rest}
        >
          {children}
        </button>
      </div>
    </div>
  );
};

export default ShellButton;

// Example usage:
// <ShellButton href="/contact">Contact Sales</ShellButton>
// <ShellButton href="/signup" className="text-lg">Sign Up Now</ShellButton>
// <ShellButton href="/contact" variant="gradient">Contact Sales</ShellButton>
// <ShellButton href="/checkout" variant="sale">Buy Now</ShellButton>